import { filter } from "lodash";
import { Component } from "react";
import { Translation } from "react-i18next";
import { ConnectedProps, connect } from "react-redux";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { Option } from "space-monad";
import styled from "styled-components";
import { TextField } from "../../../../components/forms/text";
import { Flex } from "../../../../components/layout";
import { Spinner } from "../../../../components/loader/icon";
import { H1 } from "../../../../components/typography";
import { Widget } from "../../../../components/widget";
import { Pageable, Sort } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { BillingActions } from "../../../../store/billing/actions";
import { BillingDTO } from "../../../../store/billing/models/dto";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../../../store/common/fetchable/helpers";
import { AnErrorHasOccured } from "./base";
import { PresentationalTransactionsList } from "./results";

const Header = styled(Flex)`
  justify-content: space-between;
`;

const Wrapper = styled(Flex).attrs({ centered: true })`
  justify-content: center;
  height: 20em;
`;

interface TransactionsComponentPropsFromState {
  allTransactions: {
    data: Fetchable<BillingDTO.Responses.GetAllTransactions.T>;
    pagination: Pageable.T<
      Exclude<keyof BillingDTO.Responses.GetSingleTransaction.T, "id">
    >;
  };
}

type SetPaginationFunc<K> = (
  search: string,
  sorting?: Sort<keyof K>
) => BillingActions.Actions;

type TransactionsComponentPropsFromDispatch<K> = {
  setAllTransactionsPagination: (
    fields: Array<keyof K>
  ) => SetPaginationFunc<K>;
};

type TransactionsComponentPropsFromMerge<
  K
> = TransactionsComponentPropsFromState & {
  setAllTransactionsPagination: SetPaginationFunc<K>;
};

const connection = connect<
  TransactionsComponentPropsFromState,
  TransactionsComponentPropsFromDispatch<
    Omit<BillingDTO.Responses.GetSingleTransaction.T, "id">
  >,
  {},
  TransactionsComponentPropsFromMerge<
    Omit<BillingDTO.Responses.GetSingleTransaction.T, "id">
  >,
  AppStore
>(
  (store: AppStore) => ({
    allTransactions: store.billing.allTransactions,
  }),
  (dispatch) => ({
    setAllTransactionsPagination: (
      fields: Array<keyof BillingDTO.Responses.GetSingleTransaction.T>
    ) => (value: string, sorting) =>
      dispatch(
        BillingActions.setAllTransactionsPagination({
          filters: filter(
            fields.map((field) => ({ field, value })),
            ({ value }) => value !== ""
          ),
          sorting,
        })
      ),
  }),
  (stateProps, dispatchProps) => ({
    ...stateProps,
    setAllTransactionsPagination: dispatchProps.setAllTransactionsPagination([
      "transactionHash",
      "buyerEmailAddress",
    ]),
  }),
  {}
);

export type TransactionsComponentProps = ConnectedProps<typeof connection>;

type TransactionsComponentState = {
  loading: boolean;
  search: string;
  debouncedSearch: string;
};

class TransactionsComponentInner extends Component<
  TransactionsComponentProps,
  TransactionsComponentState
> {
  readonly subscriptions: Subscription[] = [];
  readonly searchSubject;

  constructor(props: TransactionsComponentProps) {
    super(props);
    this.searchSubject = new Subject<string>();

    this.subscriptions.push(
      this.searchSubject.pipe(debounceTime(500)).subscribe((value) => {
        this.props.setAllTransactionsPagination(
          value,
          this.props.allTransactions.pagination.sorting
        );
        this.setState({ debouncedSearch: value });
      })
    );
  }

  componentDidMount() {
    if (!isFetchableSuccess(this.props.allTransactions.data)) {
      this.props.setAllTransactionsPagination("", {
        field: "purchaseTime",
        direction: "desc",
      });
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  state = {
    loading: false,
    ...Option(
      (isFetchableSuccess(this.props.allTransactions.data) &&
        this.props.allTransactions.pagination.filters?.find(
          (x) => x.field === "buyerEmailAddress"
        )?.value) ||
        ""
    ).fold(
      () => ({
        search: "",
        debouncedSearch: "",
      }),
      (val) => ({
        search: val,
        debouncedSearch: val,
      })
    ),
  };

  render() {
    const { allTransactions } = this.props;

    let componentToRender = null;

    if (allTransactions.data.status === "error") {
      componentToRender = <AnErrorHasOccured />;
    } else if (allTransactions.data.status === "empty") {
      componentToRender = (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    } else if (allTransactions.data.status === "ready") {
      componentToRender = (
        <PresentationalTransactionsList
          transactionsList={allTransactions.data.data}
        />
      );
    }

    if (this.state.debouncedSearch !== this.state.search) {
      componentToRender = (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <Widget>
        <Header>
          <H1 className="h1">
            <Translation ns="billing">{(t) => t("purchases")}</Translation>
          </H1>
        </Header>
        <TextField
          value={this.state.search}
          onChange={(e) => {
            this.setState({ search: e.target.value });
            this.searchSubject.next(e.target.value);
          }}
          fullWidth
          type="text"
          label={<Translation ns="common">{(t) => t("search")}</Translation>}
        />
        {componentToRender}
      </Widget>
    );
  }
}

export const AdminTransactionsAll = connection(TransactionsComponentInner);
